<template>
  <GtcModal
    :open="open"
    :closable="showCloseAndCancel"
    :mask-closable="showCloseAndCancel"
    :title="t('modals.common.verifyPhone.title')"
    :confirm-loading="confirmLoading"
    width="466px"
    :show-cancel-button="showCloseAndCancel"
    :ok-text="t('modals.common.verifyPhone.form.button.submitText')"
    :cancel-text="t('modals.common.verifyPhone.form.button.cancelText')"
    @ok="handleOk"
    @cancel="$emit('cancel')"
  >
    <a-form
      ref="formRef"
      class="w-full !my-6"
      :model="state.formData"
      :rules="rules"
      layout="vertical"
      :required-mark="false"
      @finish="handleOk"
    >
      <a-form-item :label="t('modals.common.verifyPhone.form.phone.label')" name="phone">
        <HPhoneInput
          size="xl"
          v-model="state.formData.phone"
          v-model:phoneCode="state.formData.d_code"
          :button-text="t('modals.common.verifyPhone.form.button.sendVerificationCode')"
          :show-error="false"
        />
      </a-form-item>
      <a-form-item name="phoneCode">
        <div class="flex items-center justify-between mb-[14px]">
          <div>{{ t('modals.common.verifyPhone.form.phoneCode.label') }}</div>
          <SendCodeButton
            :disabled="!state.formData.d_code || !state.formData.phone"
            size="small"
            type="link"
            v-model:phone="state.formData.phone"
            api-url="/api/member/get_update_phone_code"
            target-type="phone"
            target-field="phone"
            :api-params="{
              d_code: `+${state.formData.d_code}`,
              phone: state.formData.phone,
            }"
            @send-success="onSendSuccess"
          >
            <template #default="{ buttonText }">
              <div
                :class="
                  !state.formData.d_code || !state.formData.phone
                    ? 'ink-12 text-ink-500 cursor-not-allowed'
                    : 'ink-12 text-primary-500 cursor-pointer'
                "
              >
                {{ buttonText }}
              </div>
            </template>
          </SendCodeButton>
        </div>
        <VerifyCodeInput v-model="state.formData.phoneCode" />
      </a-form-item>
    </a-form>
  </GtcModal>
</template>

<script setup lang="ts">
import { computed, ref, reactive } from 'vue'
import { useLocalStorage } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { GtcModal, VerifyCodeInput, HPhoneInput, SendCodeButton } from '@/components'
import useUserStore from '@/store/user/user'
import type { FormInstance } from 'ant-design-vue'

interface UpdatePhoneParams {}

const props = defineProps<{
  open: boolean
  avatar: string
  d_code: string
  phone: string
  canClose?: boolean
  loading?: boolean
}>()
const emit = defineEmits<{
  cancel: []
  confirm: [data: UpdatePhoneParams]
}>()

const { t } = useI18n()

const state = reactive({
  formData: {
    d_code: props.d_code,
    phone: props.phone,
    phoneCode: '',
  },
})

const formRef = ref<FormInstance | null>(null)
const confirmLoading = computed(() => !!props.loading)

const userStore = useUserStore()
const counts = useLocalStorage<Record<string, number>>('verify_phone_send_counts', {})
const uid = userStore.userId
const currentCount = computed(() => (uid ? (counts.value[uid] ?? 0) : 0))
const showCloseAndCancel = computed(() => currentCount.value > 1)

const onSendSuccess = () => {
  if (!uid) return
  const next = (counts.value[uid] ?? 0) + 1
  counts.value[uid] = next
}

const rules = computed(() => {
  return {
    phone: [
      {
        required: true,
        message: t('modals.common.verifyPhone.form.phone.rules.required'),
        trigger: ['blur', 'change'],
      },
      {
        validator: async () => {
          const code = String(state.formData.d_code ?? '').replace(/\D/g, '')
          const num = String(state.formData.phone ?? '').replace(/\D/g, '')

          // 校验国家码和手机号是否都已填写且完整
          if (!code || !num) {
            return Promise.reject(t('modals.common.verifyPhone.form.phone.rules.incomplete'))
          }

          // 国家码通常为 1~3 位且首位非 0（更严谨、避免误填）
          // if (!/^[1-9]\d{0,2}$/.test(code)) {
          //   return Promise.reject('国家码格式不正确')
          // }

          const e164 = `+${num}`
          // E.164（含国家码总位数 7~15；+ 后首位非 0）
          if (!/^\+[1-9]\d{5,14}$/.test(e164)) {
            return Promise.reject(t('modals.common.verifyPhone.form.phone.rules.format'))
          }

          return Promise.resolve()
        },
        trigger: ['blur', 'change'],
      },
    ],
    phoneCode: [
      {
        required: true,
        message: t('modals.common.verifyPhone.form.phoneCode.rules.required'),
        trigger: ['blur', 'change'],
      },
      {
        validator: async (rule: any, value: any) => {
          if (value && value.length !== 6) {
            return Promise.reject(t('modals.common.verifyPhone.form.phoneCode.rules.length'))
          }
          return Promise.resolve()
        },
        trigger: ['blur', 'change'],
      },
    ],
  }
})

const handleOk = () => {
  if (confirmLoading.value) return
  formRef.value?.validate().then(() => {
    emit('confirm', {
      d_code: `+${state.formData.d_code}`,
      phone: state.formData.phone,
      phone_code: state.formData.phoneCode,
    })
  })
}
</script>
